// api/config.ts
import Constants from 'expo-constants';
import * as Updates from 'expo-updates';

// The API base and app domain can come from Expo config (app.json/app.config)
// or from EXPO_PUBLIC_* env vars in .env during local dev
// Prefer .env (EXPO_PUBLIC_*) during local dev; fall back to Expo extra
// On web, prefer the current origin for dev/localhost to avoid pointing to production
const webFallbackOrigin = typeof window !== 'undefined' ? window.location?.origin : undefined;
const webHost = typeof window !== 'undefined' ? window.location?.hostname : undefined;
const appVariant = (Constants.expoConfig?.extra as any)?.APP_VARIANT || process.env.APP_VARIANT;
export const APP_VARIANT = appVariant || 'development';
const updateChannel = (Updates as any)?.channel || (Updates as any)?.releaseChannel;
const isProductionChannel = updateChannel === 'production';

const forcedApiBase = isProductionChannel
  ? 'https://app.alldrivers.pt'
  : undefined;

const variantDefaultApiBase =
  appVariant === 'preview' || appVariant === 'development'
    ? 'https://dev.alldrivers.pt'
    : undefined;

const explicitApiBase =
  forcedApiBase ||
  process.env.EXPO_PUBLIC_API_URL ||
  (Constants.expoConfig?.extra as any)?.API_BASE_URL ||
  undefined;
const isLocalWebHost = !!webHost && /^(localhost|127\.0\.0\.1|192\.168\.\d{1,3}\.\d{1,3}|10\.\d{1,3}\.\d{1,3}\.\d{1,3}|172\.(1[6-9]|2\d|3[0-1])\.\d{1,3}\.\d{1,3})$/i.test(webHost);
const shouldUseWebOrigin = !!webFallbackOrigin && !!webHost && (
  isLocalWebHost || webHost.includes('dev.')
);
const API_BASE_URL =
  explicitApiBase ||
  (shouldUseWebOrigin ? webFallbackOrigin : undefined) ||
  variantDefaultApiBase ||
  webFallbackOrigin ||
  'https://app.alldrivers.pt';

// Public app domain for deep links and hosted pages
const forcedAppDomain = isProductionChannel
  ? 'app.alldrivers.pt'
  : undefined;

const variantDefaultAppDomain =
  appVariant === 'preview' || appVariant === 'development'
    ? 'dev.alldrivers.pt'
    : undefined;

export const APP_DOMAIN: string =
  forcedAppDomain ||
  process.env.EXPO_PUBLIC_APP_DOMAIN ||
  (Constants.expoConfig?.extra as any)?.APP_DOMAIN ||
  variantDefaultAppDomain ||
  'app.alldrivers.pt';
// Hosted Checkout must return to the origin that actually served the web app.
// This is especially important for local LAN testing, where APP_DOMAIN may
// point at the backend port rather than the frontend/proxy port.
export const APP_ORIGIN: string = shouldUseWebOrigin && webFallbackOrigin
  ? webFallbackOrigin
  : APP_DOMAIN.startsWith('http')
    ? APP_DOMAIN
    : `https://${APP_DOMAIN}`;
export const RETURN_URL: string = `${APP_ORIGIN}/dashboard/home`;

// Ensure API_BASE_URL has protocol; default to HTTPS for safety in production/mobile
const ensureProtocol = (url: string) => {
  const trimmed = String(url || '').trim();
  if (!trimmed) return 'https://app.alldrivers.pt';
  if (!trimmed.startsWith('http://') && !trimmed.startsWith('https://')) {
    return `https://${trimmed}`;
  }
  return trimmed;
};

const finalApiBaseUrl = ensureProtocol(API_BASE_URL).replace(/\/$/, '');

// Export API base URL for components that need to build backend URLs
export const API_BASE = finalApiBaseUrl;

// Helper to build payment confirmation URL used across web and native
export const buildPaymentConfirmUrl = (clientSecret: string, redirectUrl?: string): string => {
  const base = `${finalApiBaseUrl}/payments/confirm/${clientSecret}`;
  if (redirectUrl) {
    const normalizedRedirect = redirectUrl.startsWith('/')
      ? `${APP_ORIGIN}${redirectUrl}`
      : redirectUrl;
    const encoded = encodeURIComponent(normalizedRedirect);
    return `${base}?redirect=${encoded}`;
  }
  return base;
};

// Export a single set of API endpoints derived from the base URL
export const API_ENDPOINTS = {
  // Auth endpoints
  LOGIN: `${finalApiBaseUrl}/auth/api/login/`,
  LOGOUT: `${finalApiBaseUrl}/auth/api/logout/`,
  PASSWORD_RESET: `${finalApiBaseUrl}/auth/api/password/reset/`,
  PASSWORD_RESET_CONFIRM: `${finalApiBaseUrl}/auth/api/password/reset/confirm/`,
  PASSWORD_RESET_VERIFY: `${finalApiBaseUrl}/auth/api/password/reset/verify-token/`,
  PASSWORD_CHANGE: `${finalApiBaseUrl}/auth/api/password/change/`,
  MAGIC_LINK_REQUEST: `${finalApiBaseUrl}/api/auth/magic-link/request/`,
  MAGIC_LINK_VERIFY: `${finalApiBaseUrl}/api/auth/magic-link/verify/`,

  // User endpoints
  USER_DETAILS: `${finalApiBaseUrl}/api/users/me/`,
  ONBOARDING_STATE: `${finalApiBaseUrl}/api/onboarding/state`,
  USER_NOTIFICATION_PREFERENCES: `${finalApiBaseUrl}/api/users/notification-preferences/`,
  USER_LANGUAGE: `${finalApiBaseUrl}/api/users/me/language/`,
  // Two-step email change (#188)
  EMAIL_CHANGE_REQUEST: `${finalApiBaseUrl}/api/users/me/email/change-request/`,
  EMAIL_CHANGE_STATUS: `${finalApiBaseUrl}/api/users/me/email/change-status/`,
  
  // Company endpoints
  ADD_COMPANY: `${finalApiBaseUrl}/api/companies/driver/associate/`,
  // Common NIPC lookup (MR !388 final decision 2): name + tax id when known,
  // {found:false} otherwise. Fail-closed read — never owner/license/invite state.
  COMPANY_LOOKUP: `${finalApiBaseUrl}/api/companies/lookup/`,
  REQUEST_COMPANY_TRANSFER: `${finalApiBaseUrl}/api/company-transfers/`,
  COMPANIES: `${finalApiBaseUrl}/api/companies/`,
  COMPANY_DRIVERS: `${finalApiBaseUrl}/api/companies/me/drivers/`,
  INVITE_DRIVER: `${finalApiBaseUrl}/api/companies/me/drivers/invite/`,
  UPDATE_DRIVER: `${finalApiBaseUrl}/api/companies/me/drivers/`,
  BULK_ACTIVATE_DRIVERS: `${finalApiBaseUrl}/api/companies/me/drivers/bulk-activate/`,
  BULK_TRANSFER_DRIVERS: `${finalApiBaseUrl}/api/companies/me/drivers/bulk-transfer/`,
  OWNER_DRIVER_WEEKLY_REPORT_PDF: (employmentId: string | number, weekStart?: string, includeHidden = false) => {
    const base = `${finalApiBaseUrl}/api/companies/me/drivers/${employmentId}/weekly-driving-report/pdf/`;
    const params = new URLSearchParams();
    if (weekStart) params.set('week_start', weekStart);
    if (includeHidden) params.set('include_hidden', 'true');
    const query = params.toString();
    return query ? `${base}?${query}` : base;
  },
  PENDING_INCOMING_TRANSFERS: `${finalApiBaseUrl}/api/company-transfers/me/pending-incoming/`,
  OUTGOING_TRANSFERS: `${finalApiBaseUrl}/api/company-transfers/me/outgoing/`,
  
  // Vehicle endpoints
  DRIVER_VEHICLES: `${finalApiBaseUrl}/api/vehicles/driver/`,
  VEHICLES: `${finalApiBaseUrl}/api/vehicles/`,
  
  // Time tracking endpoints
  TIMELOGS: `${finalApiBaseUrl}/api/timelogs/`,
  
  // Report endpoints
  REPORTS: `${finalApiBaseUrl}/api/reports/`,
  
  // Owner driver-profile boundary; activation is a separate explicit onboarding outcome.
  COMPLETE_DRIVER_PROFILE: `${finalApiBaseUrl}/api/companies/me/complete-driver-profile/`,
  ACTIVATE_DRIVER_PROFILE: `${finalApiBaseUrl}/api/companies/me/activate-driver-profile/`,
  
  // New endpoint
  REGISTER_DRIVER: `${finalApiBaseUrl}/api/users/register-driver/`,
  CHECK_EMAIL: `${finalApiBaseUrl}/api/users/check-email/`,
  CHECK_NIPC: `${finalApiBaseUrl}/api/users/check-nipc/`,
  OWNER_SUPPORT_REQUEST: `${finalApiBaseUrl}/api/users/owner-support-request/`,
  CHECK_NIF: `${finalApiBaseUrl}/api/users/check-nif/`,
  CHECK_LICENSE: `${finalApiBaseUrl}/api/users/check-license/`,
  SUBSCRIPTIONS: `${finalApiBaseUrl}/api/subscriptions/`,
  CANCEL_SUBSCRIPTION: `${finalApiBaseUrl}/api/subscriptions/cancel_subscription/`,
  CHECKOUT_SESSION: `${finalApiBaseUrl}/api/subscriptions/checkout_session/`,
  INVOICES: (subscriptionId: string) => `${finalApiBaseUrl}/api/subscriptions/${subscriptionId}/invoices/`,
  // Invitation endpoints
  DRIVER_INVITATION: `${finalApiBaseUrl}/api/companies/invitations/accept/`,
  // #207 matrix C2: match a pending invitation by the driver's own email.
  INVITATION_BY_EMAIL: `${finalApiBaseUrl}/api/companies/invitations/by-email/`,
  // #207 matrix C5: ask the owner for a fresh invite after this one expired.
  INVITATION_REQUEST_RENEWAL: (token: string) =>
    `${finalApiBaseUrl}/api/companies/invitations/${token}/request-renewal/`,
  UPDATE_PAYMENT_METHOD: `${finalApiBaseUrl}/api/subscriptions/update_payment_method/`,
  BILLING_PORTAL: `${finalApiBaseUrl}/api/subscriptions/billing_portal/`,
  
  // Registration settings endpoints
  REGISTRATION_SETTINGS: `${finalApiBaseUrl}/api/registration-settings`,
  // Server-driven paywall config (#251)
  PAYWALL_CONFIG: `${finalApiBaseUrl}/api/paywall/config`,
  // Driver self-pay checkout (#207) — server-driven, card upfront.
  // No client "verify" endpoint: entitlement is granted + tenant-verified by the
  // Stripe webhook; the return screen reads SUBSCRIPTION_STATUS (server-authoritative).
  SELF_PAY_CHECKOUT: `${finalApiBaseUrl}/api/subscriptions/self-pay/checkout/`,
  // Anonymous aggregate funnel counters (#100) — no auth, no identifiers
  FUNNEL_METRIC: `${finalApiBaseUrl}/api/metrics/funnel/`,
  // Paywall state machine (#209)
  SUBSCRIPTION_STATUS: `${finalApiBaseUrl}/api/subscriptions/status/`,
  // reCAPTCHA verification endpoint
  VERIFY_RECAPTCHA: `${finalApiBaseUrl}/api/verify-recaptcha/`,
  
  // GPS Tracking endpoints
  GPS_TRACKING: `${finalApiBaseUrl}/api/tracking`,

  // Phone verification endpoints
  PHONE_REQUEST_OTP: `${finalApiBaseUrl}/api/users/phone/request-otp/`,
  PHONE_CONFIRM_OTP: `${finalApiBaseUrl}/api/users/phone/confirm-otp/`,

  // Support-bot association endpoints (primarily for bot-side usage)
  BOT_ASSOCIATE_INITIATE: `${finalApiBaseUrl}/api/support/bot/associate/initiate/`,
  BOT_ASSOCIATE_IDENTIFY: `${finalApiBaseUrl}/api/support/bot/associate/identify/`,
  BOT_ASSOCIATE_CONFIRM: `${finalApiBaseUrl}/api/support/bot/associate/confirm/`,
};
